import { useEffect, useMemo, useRef, useState } from 'react';
import { floorToBucket, timeframeToSeconds } from '../lib/candleAggregation';
const drawingStrokeByTool = {
    trend: '#9dffd5',
    ray: '#c8ffe8',
    horizontal: '#7eff70',
    vertical: '#7ad3ff',
    rectangle: '#ffd96f',
    arrow: '#b8f08f',
    text: '#e5f7ff',
    freehand: '#9ce8ff'
};
export function DrawingLayer({ enabled, tool, resetKey, viewportVersion, getCoordinates, getPriceTimeFromCoordinates, timeframe = 'Tick' }) {
    const [drawings, setDrawings] = useState([]);
    const [draft, setDraft] = useState(null);
    const [cursorPoint, setCursorPoint] = useState(null);
    const isPointerDownRef = useRef(false);
    useEffect(() => {
        setDrawings([]);
        setDraft(null);
        setCursorPoint(null);
    }, [resetKey]);
    useEffect(() => {
        if (!enabled) {
            setDraft(null);
            setCursorPoint(null);
            isPointerDownRef.current = false;
        }
    }, [enabled]);
    const handlePointerDown = (event) => {
        if (!enabled) {
            return;
        }
        const point = toPriceTimePoint(event, getPriceTimeFromCoordinates);
        if (!point) {
            return;
        }
        if (tool === 'horizontal' || tool === 'vertical' || tool === 'text') {
            const nextDrawing = buildSinglePointDrawing(tool, point);
            setDrawings((previous) => [...previous, nextDrawing].slice(-120));
            setDraft(null);
            return;
        }
        if (tool === 'freehand') {
            isPointerDownRef.current = true;
            setDraft({
                id: createDrawingId(),
                tool: 'freehand',
                points: [point]
            });
            return;
        }
        if (!draft) {
            setDraft({
                id: createDrawingId(),
                tool,
                start: point
            });
            setCursorPoint(point);
            return;
        }
        setDrawings((previous) => [...previous, { ...draft, end: point }].slice(-120));
        setDraft(null);
        setCursorPoint(null);
    };
    const handlePointerMove = (event) => {
        if (!enabled) {
            return;
        }
        const point = toPriceTimePoint(event, getPriceTimeFromCoordinates);
        if (!point) {
            return;
        }
        setCursorPoint(point);
        if (tool !== 'freehand' || !isPointerDownRef.current) {
            return;
        }
        setDraft((previous) => {
            if (!previous || previous.tool !== 'freehand') {
                return previous;
            }
            const lastPoint = previous.points[previous.points.length - 1];
            if (!lastPoint) {
                return previous;
            }
            if (Math.abs(lastPoint.time - point.time) < 1 && Math.abs(lastPoint.price - point.price) < 2) {
                return previous;
            }
            return {
                ...previous,
                points: [...previous.points, point]
            };
        });
    };
    const handlePointerUp = () => {
        if (!enabled) {
            return;
        }
        if (tool === 'freehand') {
            isPointerDownRef.current = false;
            setDraft((previous) => {
                if (!previous || previous.tool !== 'freehand') {
                    return null;
                }
                if (previous.points.length < 2) {
                    return null;
                }
                setDrawings((history) => [...history, previous].slice(-120));
                return null;
            });
        }
    };
    const renderedDrawings = useMemo(() => {
        const current = [...drawings];
        if (draft && draft.tool !== 'freehand' && draft.start && cursorPoint) {
            current.push({
                ...draft,
                end: cursorPoint,
                isDraft: true
            });
        }
        if (draft && draft.tool === 'freehand') {
            current.push({
                ...draft,
                isDraft: true
            });
        }
        return current.map((drawing) => toRenderableShape(drawing, getCoordinates, timeframe, viewportVersion));
    }, [cursorPoint, draft, drawings, getCoordinates, timeframe, viewportVersion]);
    return (<div className={`drawing-layer ${enabled ? 'is-enabled' : ''}`} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp}>
      <svg className="drawing-layer__svg" aria-hidden="true">
        <defs>
          <marker id="drawing-arrow-head" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L8,4 L0,8 Z" fill="#b8f08f"/>
          </marker>
        </defs>
        {renderedDrawings.map((shape) => renderShape(shape))}
      </svg>
    </div>);
}
function toPriceTimePoint(event, converter) {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    return converter(x, y);
}
function buildSinglePointDrawing(tool, point) {
    if (tool === 'text') {
        return {
            id: createDrawingId(),
            tool: 'text',
            start: point,
            text: 'Note'
        };
    }
    return {
        id: createDrawingId(),
        tool,
        start: point
    };
}
function toRenderableShape(drawing, getCoordinates, timeframe) {
    if (!drawing) {
        return null;
    }
    if (drawing.tool === 'freehand') {
        const points = drawing.points
            .map((point) => resolveCoordinatesForDrawingPoint(point, timeframe, getCoordinates))
            .filter((point) => Boolean(point))
            .map((point) => `${point.x},${point.y}`);
        if (points.length < 2) {
            return null;
        }
        return {
            id: drawing.id,
            tool: drawing.tool,
            isDraft: drawing.isDraft,
            points
        };
    }
    const start = drawing.start ? resolveCoordinatesForDrawingPoint(drawing.start, timeframe, getCoordinates) : null;
    const end = drawing.end ? resolveCoordinatesForDrawingPoint(drawing.end, timeframe, getCoordinates) : null;
    if (!start) {
        return null;
    }
    return {
        id: drawing.id,
        tool: drawing.tool,
        isDraft: drawing.isDraft,
        start,
        end,
        text: drawing.text
    };
}
function renderShape(shape) {
    if (!shape) {
        return null;
    }
    const color = drawingStrokeByTool[shape.tool] ?? '#d0ffe0';
    const opacity = shape.isDraft ? 0.6 : 1;
    if (shape.tool === 'freehand') {
        return (<polyline key={shape.id} points={shape.points.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity={opacity}/>);
    }
    if (shape.tool === 'horizontal') {
        return (<line key={shape.id} x1="0" y1={shape.start.y} x2="100%" y2={shape.start.y} stroke={color} strokeWidth="1.8" opacity={opacity}/>);
    }
    if (shape.tool === 'vertical') {
        return (<line key={shape.id} x1={shape.start.x} y1="0" x2={shape.start.x} y2="100%" stroke={color} strokeWidth="1.8" opacity={opacity}/>);
    }
    if (shape.tool === 'text') {
        return (<g key={shape.id} opacity={opacity}>
          <rect x={shape.start.x + 6} y={shape.start.y - 22} width="54" height="18" rx="4" fill="rgba(0, 22, 15, 0.82)" stroke={color} strokeWidth="1"/>
          <text x={shape.start.x + 12} y={shape.start.y - 10} fill={color} fontSize="11" fontWeight="700">
            {shape.text ?? 'Note'}
          </text>
        </g>);
    }
    if (!shape.end) {
        return null;
    }
    if (shape.tool === 'rectangle') {
        const x = Math.min(shape.start.x, shape.end.x);
        const y = Math.min(shape.start.y, shape.end.y);
        const width = Math.max(2, Math.abs(shape.start.x - shape.end.x));
        const height = Math.max(2, Math.abs(shape.start.y - shape.end.y));
        return (<rect key={shape.id} x={x} y={y} width={width} height={height} fill="rgba(255, 217, 111, 0.06)" stroke={color} strokeWidth="1.8" opacity={opacity}/>);
    }
    if (shape.tool === 'ray') {
        const dx = shape.end.x - shape.start.x;
        const dy = shape.end.y - shape.start.y;
        if (Math.abs(dx) < 0.0001) {
            return (<line key={shape.id} x1={shape.start.x} y1={shape.start.y} x2={shape.start.x} y2="0" stroke={color} strokeWidth="1.8" opacity={opacity}/>);
        }
        const extension = 2200;
        const xTarget = dx > 0 ? shape.start.x + extension : shape.start.x - extension;
        const yTarget = shape.start.y + dy * ((xTarget - shape.start.x) / dx);
        return (<line key={shape.id} x1={shape.start.x} y1={shape.start.y} x2={xTarget} y2={yTarget} stroke={color} strokeWidth="1.8" opacity={opacity}/>);
    }
    if (shape.tool === 'arrow') {
        return (<line key={shape.id} x1={shape.start.x} y1={shape.start.y} x2={shape.end.x} y2={shape.end.y} stroke={color} strokeWidth="2" markerEnd="url(#drawing-arrow-head)" opacity={opacity}/>);
    }
    return (<line key={shape.id} x1={shape.start.x} y1={shape.start.y} x2={shape.end.x} y2={shape.end.y} stroke={color} strokeWidth="2" opacity={opacity}/>);
}
function createDrawingId() {
    return `drawing-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function resolveCoordinatesForDrawingPoint(point, timeframe, getCoordinates) {
    if (!point || !getCoordinates || !Number.isFinite(point.time) || !Number.isFinite(point.price)) {
        return null;
    }

    const directCoordinates = getCoordinates(point.price, point.time);
    if (directCoordinates || timeframe === 'Tick') {
        return directCoordinates;
    }

    const intervalSeconds = timeframeToSeconds(timeframe);
    const bucketTime = floorToBucket(point.time, intervalSeconds);
    const candidateTimes = [
        bucketTime,
        bucketTime + intervalSeconds,
        bucketTime - intervalSeconds
    ];

    for (const candidateTime of candidateTimes) {
        if (!Number.isFinite(candidateTime)) {
            continue;
        }
        const candidateCoordinates = getCoordinates(point.price, candidateTime);
        if (candidateCoordinates) {
            return candidateCoordinates;
        }
    }

    return null;
}
